/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useState } from 'react'
import { observer } from 'mobx-react'
import { Schema$SportIssue, Schema$SportTalkPickUserInfo } from '@interface/sport-issue'
import { PopularSearchTermPopupWrap } from '@components/PopularSearchTerm/index.style'
import { ListView } from 'antd-mobile'
import { Button } from '@styles/button.style'
import { MATCH_INSIGHT_TYPE, SPORT_ISSUE_TYPE } from '@v2/constants/sport-issue'
import { YouTubeEmbed } from 'react-social-media-embed'
import { Schema$SimpleMatchTrack } from '@interface/matchup'
// img
import popularSearchSlideImg from '@assets/images/bg/sport-issue-popular-search-slide.jpg'
import PopupUtils from '@utils/popup-utils'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { useOnMount } from '@utils/reactExt'
import { SportIssueCardWrap, SportIssuePopupListWrap } from '@views/Matchup/MatchUpDetail/index.style'
import AdvImg from '@components/AdvImg'
import { isMobile } from 'react-device-detect'
import { SwiperWrap } from '@styles/base.style'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react'
import { FreeMode, Pagination } from 'swiper'
import Login from '@views/Login'
import { AdvButton } from '@components/Button'
import { AnalysisProfile } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/AnalysisProfile'
import { FROM_VIEW_TYPE, PICK_WRITER_TYPE } from '@v2/constants/common'
import { getMatchInsightByTimeline } from '@utils/utils'
import { SPORT_ID } from 'adv-protocol/src/GameCommon'
import { TimelineEventType } from '@interface/chat'
import { ScoreTimelineItem } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/ScoreTimelineItem'
import { HalftimeTimelineItem } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/HalftimeTimelineItem'
import { SportIssueSwiper } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/SportIssueSwiper'
import DialogUtils from '@utils/dialog-utils'

export interface SportIssuePopupProps {
  track: Schema$SimpleMatchTrack
  matchId: number
  pickUserInfos: Schema$SportTalkPickUserInfo[]
}

const SportIssuePopup: React.FC<SportIssuePopupProps> = observer((props => {
  const { track, matchId, pickUserInfos } = props
  const {
    componentStore,
    authStore,
    userPickStore,
    companyPickStore,
    globalStore,
  } = useRootStore()
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [sportIssueList, setSportIssueList] = useState([])
  const isDashboardEnabled = globalStore.getSportTalkDashboardEnabled()

  const { sportId } = track

  const homeTeamEmblemUrl = track?.homeTeamEmblem
  const awayTeamEmblemUrl = track?.awayTeamEmblem
  const homeTeamName = track?.homeTeamName
  const awayTeamName = track?.awayTeamName

  const fetch = async (reset = false) => {
    try {
      setIsLoading(true)
      const result = await componentStore.fetchSportIssueList(matchId, _.size(sportIssueList), 20)
      if (reset) {
        setSportIssueList(result?.rows || [])
      } else {
        setSportIssueList(_.concat(sportIssueList, result?.rows || []))
      }
      setHasMore(result?.hasMore || false)
    } catch (err) {
      console.error(err)
    }
    setIsLoading(false)
  }

  useOnMount(() => {
    fetch().catch(console.error)
  })

  const endReachedHandler = () => {
    if (!isLoading && hasMore) {
      fetch().catch(console.error)
    }
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1: any, row2: any) => row1 !== row2,
  })

  const hasQualifiedIssue = sportIssueList.some(item =>
    item.sportIssueType === SPORT_ISSUE_TYPE.POPULAR_SEARCH_TERM_NEWS ||
    (item.matchInsight && item.matchInsight.matchInsightType !== MATCH_INSIGHT_TYPE.TIMELINE),
  )

  const renderHeader = (): React.ReactElement => {
    if (isDashboardEnabled && hasQualifiedIssue) {
      return <p className="popup-title">스포츠 이슈</p>
    }
    return <Fragment/>
  }

  const renderRow = (item: Schema$SportIssue) => {
    let contents: JSX.Element
    let titleTxt: string
    let onClickSportIssueDetail: (() => void) | null = null
    if (item.sportIssueType === SPORT_ISSUE_TYPE.POPULAR_SEARCH_TERM_NEWS) {
      contents = <AdvImg src={popularSearchSlideImg} srcMode={'small'} alt="now 이슈키워드" className="thumbnail"/>
      titleTxt = item.content
      onClickSportIssueDetail = () => {
        PopupUtils.showPopularSearchTermPopup(item.popularSearchTerm)
      }
    } else {
      const { matchInsightType, attachment } = item.matchInsight
      if (matchInsightType === MATCH_INSIGHT_TYPE.IMAGE) {
        contents = <AdvImg src={attachment} srcMode={'small'} alt={item.content} className="thumbnail"/>
        titleTxt = item.content
        onClickSportIssueDetail = () => {
          DialogUtils.showSportIssueContent({
            custom: (
              <SportIssueSwiper data={sportIssueList} id={item.matchInsight.id} scoreData={{
                homeTeamName,
                awayTeamName,
                homeTeamEmblemUrl,
                awayTeamEmblemUrl,
                sportId,
              }}/>
            ),
          })
        }
      } else if (matchInsightType === MATCH_INSIGHT_TYPE.URL) {
        contents = <div className="embed-wrap thumbnail"><YouTubeEmbed url={attachment} width={'252px'} height={'140px'}/></div>
        titleTxt = item.content
        onClickSportIssueDetail = () => {
          DialogUtils.showSportIssueContent({
            custom: (
              <SportIssueSwiper data={sportIssueList} id={item.matchInsight.id} scoreData={{
                homeTeamName,
                awayTeamName,
                homeTeamEmblemUrl,
                awayTeamEmblemUrl,
                sportId,
              }}/>
            ),
          })
        }
      } else if (matchInsightType === MATCH_INSIGHT_TYPE.TIMELINE) {
        // 0010228: [V2공통] 타임라인 생성 시_스포츠 정보 자동 생성_기능_추가
        const matchInsight = getMatchInsightByTimeline({
          timeline: item.matchInsight.timeline.content,
          homeTeamName,
          awayTeamName,
          homeTeamEmblemUrl,
          awayTeamEmblemUrl,
        })
        const {
          eventType, scoreTeamName, scoreTime, goalScorer, emblemUrl, homeTeamScore, awayTeamScore,
        } = matchInsight
        if (sportId === SPORT_ID.SOCCER) {
          if (eventType === TimelineEventType.SCORE_CHANGE) {
            {/************* 스포츠정보_득점 타임라인 *************/}
            contents = (
              <ScoreTimelineItem className={'thumbnail'} scoreTimelineData={{
                teamName: scoreTeamName,
                time: scoreTime,
                playerName: goalScorer,
                teamEmblemUrl: emblemUrl,
              }}/>)
            titleTxt = `${homeTeamName} ${homeTeamScore}:${awayTeamScore} ${awayTeamName}`
            onClickSportIssueDetail = () => {
              DialogUtils.showSportIssueContent({
                custom: (
                  <SportIssueSwiper
                    data={sportIssueList}
                    id={item.matchInsight.id}
                    scoreData={{
                      homeTeamName,
                      awayTeamName,
                      homeTeamEmblemUrl,
                      awayTeamEmblemUrl,
                      sportId,
                    }}
                  />
                ),
              })
            }
          } else if (eventType === TimelineEventType.PERIOD_SCORE) {
            {/************* 스포츠정보_전반전 종료 타임라인 *************/}
            contents = (
              <HalftimeTimelineItem className={'thumbnail'} scoreTimelineData={{
                homeTeamEmblemUrl: homeTeamEmblemUrl,
                awayTeamEmblemUrl: awayTeamEmblemUrl,
                homeTeamName: homeTeamName,
                awayTeamName: awayTeamName,
                homeTeamScore: homeTeamScore,
                awayTeamScore: awayTeamScore,
                sportId: sportId,
              }}/>)
            titleTxt = `${homeTeamName} ${homeTeamScore}:${awayTeamScore} ${awayTeamName}`
            onClickSportIssueDetail = () => {
              DialogUtils.showSportIssueContent({
                custom: (
                  <SportIssueSwiper
                    data={sportIssueList}
                    id={item.matchInsight.id}
                    scoreData={{
                      homeTeamName,
                      awayTeamName,
                      homeTeamEmblemUrl,
                      awayTeamEmblemUrl,
                      sportId,
                    }}
                  />
                ),
              })
            }
          }
        }
      }
    }

    return (
      <SportIssuePopupListWrap>
        <Button
          TP
          onClick={onClickSportIssueDetail}
          className="popular-search-news-btn"
        >
          {contents}
          <p className="title">{titleTxt}</p>
        </Button>
      </SportIssuePopupListWrap>
    )
  }

  const makeAnalysisSlidesSlides = () => {
    const items = pickUserInfos || []
    return _.map(items, (item, index) => {
      const { targetId, userPromiseId } = item

      const onClickAnalysisBtn = async () => {
        if (!authStore.isSignedIn) {
          authStore.showLoginPopup(<Login/>)
        } else {
          if (item.pickWriterType === PICK_WRITER_TYPE.USER_PICK) { // s 멤버십 경기분석 팝업창
            try {
              await userPickStore.fetchMatch(matchId)
              PopupUtils.showUserPickList({
                targetUserPickId: targetId,
                fromView: FROM_VIEW_TYPE.SPORT_TALK,
              })
            } catch (error) {
              console.error(error)
            }
          } else { //프리미엄 분석가 경기분석 팝업창
            try {
              await companyPickStore.fetchCompanyPick(targetId)
              await companyPickStore.fetchCompanyPickManagers({
                userPromiseIds: [userPromiseId],
              })
              PopupUtils.showCompanyPickDetailPopup()
            } catch (error) {
              console.error(error)
            }
          }
        }
      }

      return (
        <SwiperSlide className="swipe-item analysis-profile" key={index}>
          <AdvButton
            width={'100%'}
            contents={<AnalysisProfile userInfo={item} lightMode/>}
            onClick={onClickAnalysisBtn}
          />
        </SwiperSlide>
      )
    })
  }

  let sportIssueListContents: JSX.Element
  const analysisSlides = makeAnalysisSlidesSlides()
  if (isMobile) {
    sportIssueListContents = (
      <SwiperWrap className="quick-swipe">
        {analysisSlides}
      </SwiperWrap>
    )
  } else {
    sportIssueListContents = (
      <SwiperWrap className="quick-swipe right-navigation">
        <Swiper
          slidesPerView={'auto'}
          pagination={!isMobile && _.size(analysisSlides) > 0 && { type: 'fraction', clickable: false }}
          navigation={!isMobile}
          spaceBetween={14}
          loop={false}
          shortSwipes
          modules={[FreeMode, Pagination]}
        >
          {analysisSlides}
        </Swiper>
      </SwiperWrap>
    )
  }

  const renderFooter = () => {
    if (isDashboardEnabled && _.size(analysisSlides) !== 0) {
      return (
        <Fragment>
          <p className="popup-title">추천 분석글</p>
          <SportIssueCardWrap className={!isMobile && 'pc'}>
            {sportIssueListContents}
          </SportIssueCardWrap>
        </Fragment>
      )
    }
    return <Fragment/>
  }

  return (
    <PopularSearchTermPopupWrap>
      <ListView
        dataSource={dataSource.cloneWithRows(sportIssueList || [])}
        renderHeader={renderHeader}
        renderRow={renderRow}
        renderFooter={renderFooter}
        pageSize={20}
        initialListSize={20}
        onEndReached={endReachedHandler}
        css={{ height: '100%' }}
      />
    </PopularSearchTermPopupWrap>
  )

}))
export default SportIssuePopup
